import { useState, type ReactNode } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { Header } from '../header';
import { Footer } from '../footer';
import { Layout } from '../layout';
import { ExpandGroup, EXPAND_STEP_ROOMY } from '../expand';
import { CONTACT_NGO_IDS, ContactInfo } from './info';
import { ContactPanel } from './panel';
import type { Audience, NgoTab } from './audiences';

/**
 * Both halves of the logo strip, since the audience is unknown here. `prefetchQuery`, not
 * `ensureQueryData`: a floating rejected promise takes the server process down (see the homepage loader).
 */
export function prefetchContactPage(context: { queryClient: QueryClient }) {
	context.queryClient.prefetchQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	context.queryClient.prefetchQuery(trpc.portal.getNgoLogos.queryOptions({ ngoIds: [...CONTACT_NGO_IDS] }));
}

/**
 * The shell of the four audience pages, owned by the parent route so switching audience does not replay
 * the `Expand` entrance, which reads as "the page refreshes". `children` is the child's `<Outlet />`.
 */
export function ContactPage(props: { audience: Audience | null; tab: NgoTab; children: ReactNode }) {
	// Not a boolean: the shell outlives an audience switch, and `sent` must not carry over.
	const [sentFor, setSentFor] = useState<Audience | null>(null);
	const sent = sentFor !== null && sentFor === props.audience;

	return (
		<Layout>
			<Header />
			{props.children}
			<ExpandGroup step={EXPAND_STEP_ROOMY} asChild>
				<section className="mx-auto grid max-w-[1440px] items-start gap-6 px-6 pt-4 pb-16 md:pb-24 lg:grid-cols-[793fr_559fr] xl:px-8">
					<ContactInfo audience={props.audience} sent={sent} />
					<ContactPanel
						audience={props.audience}
						tab={props.tab}
						sent={sent}
						onSent={() => setSentFor(props.audience)}
					/>
				</section>
			</ExpandGroup>
			<Footer />
		</Layout>
	);
}
